// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel from "@turnerBlueprint/App/Components/XngCarousel";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@turnerBlueprint/MobiusOverrides/LazyImage";
import * as React from "react";
import { connect } from "react-redux";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface ProductListSubCategoriesStyles {
    container?: InjectableCss;
    subCategoryItem?: InjectableCss;
    subCategoryImageItem?: InjectableCss;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: InjectableCss;
    subCategoryNameLink?: LinkPresentationProps;
    innerInformationContainer?: InjectableCss;
}

export const productListSubCategoriesStyles: ProductListSubCategoriesStyles = {};

const styles = productListSubCategoriesStyles;

const ProductListSubCategories = ({ category, fields: { showImages } }: Props) => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }
    const CategorySlides = category.subCategoryIds.map(subCategoryId => (
        <StyledWrapper key={subCategoryId} {...styles.subCategoryItem}>
            <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
        </StyledWrapper>
    ));

    return (
        <StyledWrapper {...styles.container}>
            {isTabletScreen ? (
                <XngCarousel spaceBetween={16} navigation={false} scrollbar>
                    {CategorySlides}
                </XngCarousel>
            ) : (
                CategorySlides
            )}
        </StyledWrapper>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    return (
        <StyledWrapper {...styles.innerInformationContainer}>
            <StyledWrapper {...styles.subCategoryImageItem}>
                {showImages && (
                    <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                        <LazyImage
                            src={category.smallImagePath}
                            altText={category.imageAltText}
                            {...styles.subCategoryImage}
                        />
                    </Clickable>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.subCategoryNameLinkItem}>
                <Link
                    href={category.path}
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(ProductListSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
